import { IApiError } from "../api.interfaces";
import { isArgumentNullException, isInvalidOperationException } from "./error.types";

export const ERROR_MESSAGES = {
    ADDIN_INACCESSIBLE: "Could not retrieve addin",
    OVERLIMIT: "Overlimit failed call ",
    SERVER_UNAVAILABLE: "Server is unavailable ",
    NOTIFICATION_TEXT_NULL: "Notification text cannot be null",
    NOTIFICATION_TEXT_EMPTY: "Notification text cannot be empty"
};

export function isInvalidTextMessageUser(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("Invalid user Id for textMessages.") > -1;
}

export function isNotInSystemException(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("is not in the system") > -1;
}

export function isEntityRemoved(errorObject?: IApiError) {
    return errorObject && isArgumentNullException(errorObject) && errorObject.message?.indexOf("Value cannot be null") > -1 && errorObject.message?.indexOf("oldValue") > -1;
}

export function isLogAlreadySavedException(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("Cannot add DutyStatusLog with parentId populated.") > -1;
}

export function isInvalidAutomaticLog(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("Cannot add an Automatic") > -1;
}

export function isCoDriverRemovedException(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("fk_dutystatuscodriver_users") > -1;
}

export function isVehicleRemoved(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("violates foreign key constraint") > -1 && errorObject.message?.indexOf("fk_dutystatuslog_vehicle") > -1;
}

export function isInvalidRuleSetChangeLog(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("log without the UserHosRuleSet property") > -1;
}

export function isIgnoreHosLogClearanceException(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("User does not have IgnoreHOSLog clearance!") > -1;
}

export function isDriverAccessException(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("Prevent driver access to shared data") > -1;
}

export function isInvalidLogEditException(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("cannot edit log") > -1;
}

export function isUnverifiedOrUnidentifiedDriverEditRequest(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("Unable to edit the log because the log is not certified or the log owner is an unidentified driver.") > -1;
}

export function isCannotEditStatusLog(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("The log has been edited by someone else, please try again.") > -1;
}

export function isInvalidRequestedLogEditException(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("pending edit request") > -1;
}

export function isInvalidDutyStatusLogRemovalException(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("DutyStatusLog entities cannot be removed.") > -1;
}

export function isChangedRequestedByUserException(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("Cannot change the edit requested user for a DutyStatusLog manually") > -1;
}

export function isDeviceAccessException(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("User does not have access to this device") > -1;
}

export function isDriverToDriverAccessException(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("User does not have access to this driver") > -1;
}

export function isDvirMustHaveResourceException(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("Log must have a resource") > -1;
}

export function isOverlappingTrailerAttachmentException(errorObject?: IApiError) {
    return errorObject && (
        errorObject.message?.indexOf("Cannot add TrailerAttachment with ActiveFrom before or at the same time as an existing attachment") > -1 ||
        errorObject.message?.indexOf("Cannot add TrailerAttachment - new attachment starts less than 1 millisecond after existing attachment") > -1
    );
}

export function isEarlierLogException(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("Not allowed to create a DVIR Log with datetime earlier than the previous DVIR Log on the same device.") > -1;
}

export function isRepairStatusChangedException(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("The RepairStatus cannot be changed after it is set.") > -1;
}

export function isMissingCertifiedDvir(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("Previous DVIRLog on this device was not certified") > -1;
}

export function isCannotChangeDefectRepairException(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("Can't change DVIRDefect's RepairDateTime and RepairUser") > -1;
}

export function isRepairDateTimeMismatch(errorObject?: IApiError) {
    // "DateTime does not match with the database" is thrown verbatim by DVIRLogBridge.cs:417
    // with no DVIR-specific prefix. Scope is safe: Drive only reaches this path during DVIR sync.
    return errorObject && errorObject.message?.indexOf("DateTime does not match with the database") > -1;
}

export function isMissingRepairInfoException(errorObject?: IApiError) {
    // DVIRDefectBridge rejects a repaired defect that carries no RepairDateTime/RepairUser, from
    // both the add path ("...for repaired DVIRDefect.") and the set path ("...for the repaired
    // DVIRDefect."). A DVIRLog "Add" reaches the set path for any carried-over defect, because the
    // defect already exists on the server under the same id.
    return errorObject && (
        errorObject.message?.indexOf("for the repaired DVIRDefect") > -1 ||
        errorObject.message?.indexOf("for repaired DVIRDefect") > -1
    );
}

export function isCertifiedByPersonMismatch(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("CertifiedBy person does not match with the database") > -1;
}

export function hasUnrepairedRegulatedDefects(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("has unrepaired regulated defects") > -1;
}

export function isMissingDefectsOnNewDvirException(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("The new DVIRLog must contain every outstanding unregulated DVIRDefect from previous DVIRLog") > -1;
}

export function isCertifyRemarkMismatch(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("CertifyRemark does not match with the database") > -1;
}

export function isCertifyDateMismatch(errorObject?: IApiError) {
    return errorObject && errorObject.message?.indexOf("CertifyDate does not match with the database") > -1;
}

export function isCertificationConflict(errorObject?: IApiError) {
    return isCertifyRemarkMismatch(errorObject) || isCertifyDateMismatch(errorObject);
}

export function deviceNotInUserScopeException(errorObject?: IApiError) {
    return errorObject && isInvalidOperationException(errorObject) && errorObject.message?.indexOf("Device is not in user scope.") > -1;
}

export function isExemptionPermissionException(errorObject?: IApiError) {
    return errorObject && (
        errorObject.message?.indexOf("who does not have Personal Conveyance enabled") > -1 ||
        errorObject.message?.indexOf("who does not have Yard Move enabled") > -1
    );
}
